// src/components/layout/ChatColumn/UserBubble.jsx
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, Palette } from 'lucide-react'; // Added Palette icon
import ActionButtons from './ActionButtons';
import MessageParser from './MessageParser';
import { authManager } from '@/managers/AuthManager';
import { parseFirestoreTimestamp } from '@/utils/timeUtils';

// New utility to convert HEX to RGB array
/* ========================
 * hexToRgb - Converts a HEX color string to an RGB array
 * Used for dynamically setting CSS variables from user-selected colors
 * ======================== */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
};

const getRelativeLuminance = ([red, green, blue]) => {
  const normalized = [red, green, blue].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return (0.2126 * normalized[0]) + (0.7152 * normalized[1]) + (0.0722 * normalized[2]);
};

const getAdaptiveBubbleTheme = (rgb) => {
  const isLightBubble = getRelativeLuminance(rgb) > 0.44;
  const backgroundAlpha = isLightBubble ? 0.96 : 0.88;
  const borderRgb = isLightBubble
    ? rgb.map((value) => Math.max(0, Math.round(value * 0.82)))
    : rgb;

  return {
    background: `rgba(${rgb.join(', ')}, ${backgroundAlpha})`,
    text: isLightBubble ? 'rgb(28, 30, 36)' : 'rgb(255, 255, 255)',
    border: `rgba(${borderRgb.join(', ')}, ${isLightBubble ? 0.55 : 0.22})`,
  };
};

const applyUserBubbleVariables = (root, rgb) => {
  const bubbleTheme = getAdaptiveBubbleTheme(rgb);
  root.style.setProperty('--user-bubble', bubbleTheme.background);
  root.style.setProperty('--user-bubble-text', bubbleTheme.text);
  root.style.setProperty('--user-bubble-border', bubbleTheme.border);
};

/* ========================
 * useAuthSettings - Custom hook to manage user authentication settings
 * Subscribes to AuthManager for real-time preference updates
 * ======================== */
const useAuthSettings = () => {
  const [settings, setSettings] = useState(() => authManager.getSettings());

  useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setSettings(state.settings);
    });
    return unsubscribe;
  }, []);

  return {
    chatFontSize: settings.zPreferences?.chatFontSize || 'md',
    customUserBubbleColor: settings.zPreferences?.customUserBubbleColor || null, // Added customUserBubbleColor
  };
};

/* ========================
 * UserBubble - User message bubble component with ref forwarding
 * Now properly forwards refs for scroll positioning logic and includes color picker
 * ======================== */
const UserBubble = React.forwardRef(({
  messageId,
  contentBlocks, // This is the single source of truth for display
  fullMessageObject,
  timestamp,
  onCodeBlockClick,
  isWorkspaceAgentMessage = false,
}, ref) => {
  const { chatFontSize, customUserBubbleColor } = useAuthSettings(); // Get custom bubble color
  const contentRef = useRef(null);

  const [isExpanded, setIsExpanded] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false); // State for color picker visibility

  // State for internal color picker value
  const [currentBubbleColor, setCurrentBubbleColor] = useState(() => {
    return customUserBubbleColor || '#3B82F6'; // Default blue
  });

  // Effect to update internal color picker state when customUserBubbleColor changes from settings
  useEffect(() => {
    if (customUserBubbleColor && currentBubbleColor !== customUserBubbleColor) {
      setCurrentBubbleColor(customUserBubbleColor);
    }
  }, [customUserBubbleColor, currentBubbleColor]);
  
  const finalTimestampObject = useMemo(() => {
      return parseFirestoreTimestamp(timestamp); 
  }, [timestamp]);

  const { files, actions, hasAudio } = useMemo(() => {
      const allAttachments = [];
      const msg = fullMessageObject;
      if (msg?.files && typeof msg.files === 'object') allAttachments.push(...Object.values(msg.files));
      if (Array.isArray(msg?.images)) allAttachments.push(...msg.images);
      const mediaSource = msg?.currentMediaItems || msg?.mediaItems;
      if (Array.isArray(mediaSource)) allAttachments.push(...mediaSource);
      
      const uniqueFiles = Array.from(new Map(allAttachments.filter(Boolean).map(item => [item.id || item.name, item])).values());
      
      return {
          files: uniqueFiles,
          actions: msg?.actions || [],
          hasAudio: msg?.hasAudio || false
      };
  }, [fullMessageObject]);

  const checkOverflow = useCallback(() => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setIsOverflowing(scrollHeight > clientHeight);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [contentBlocks, files, checkOverflow, isExpanded]);

  const handleMouseEnter = () => setShowActions(true);
  const handleMouseLeave = () => {
    setShowActions(false);
    if (colorPickerRef.current && !colorPickerRef.current.contains(event.relatedTarget)) {
      setShowColorPicker(false);
    }
  };
  const handleExpandToggle = () => setIsExpanded(!isExpanded);

  const formattedTime = useMemo(() => {
    if (finalTimestampObject instanceof Date && !isNaN(finalTimestampObject.getTime())) {
      return finalTimestampObject.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return '--:--';
  }, [finalTimestampObject]);

  const handleColorChange = useCallback((event) => {
    const newHexColor = event.target.value;
    setCurrentBubbleColor(newHexColor);
    const newRgb = hexToRgb(newHexColor);
    if (newRgb) {
      authManager.updateSettings({ 
        zPreferences: { 
          customUserBubbleColor: newHexColor
        } 
      });
      const root = document.documentElement;
      applyUserBubbleVariables(root, newRgb);
    }
  }, []);

  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (customUserBubbleColor) {
      const newRgb = hexToRgb(customUserBubbleColor);
      if (newRgb) {
        const root = document.documentElement;
        applyUserBubbleVariables(root, newRgb);
      }
    } else {
      const root = document.documentElement;
      root.style.removeProperty('--user-bubble');
      root.style.removeProperty('--user-bubble-text');
      root.style.removeProperty('--user-bubble-border');
    }
  }, [customUserBubbleColor]);

  const customBubbleStyles = useMemo(() => {
    if (customUserBubbleColor && !isWorkspaceAgentMessage) {
      const rgb = hexToRgb(customUserBubbleColor);
      if (rgb) {
        const bubbleTheme = getAdaptiveBubbleTheme(rgb);
        return {
          background: bubbleTheme.background,
          color: bubbleTheme.text,
          borderColor: bubbleTheme.border,
          borderWidth: '1px',
          borderStyle: 'solid',
        };
      }
    }
    return {};
  }, [customUserBubbleColor, isWorkspaceAgentMessage]);

  // THIS IS THE GLOWING STYLE YOU WANTED
  const bubbleClassName = `
    relative rounded-2xl rounded-br-md
    transition-all duration-300 ease-in-out
    min-w-[280px] max-w-[515px] pr-4 pl-4 pt-2.5 pb-2.5
    ${isWorkspaceAgentMessage
      ? 'bg-gradient-to-br from-surface to-surface-dark border-2 border-primary/40 text-on-surface shadow-2xl shadow-primary/20'
      : 'bg-user-bubble text-user-bubble-text border border-user-bubble-border shadow-sm hover:shadow-md'
    }
  `;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className={bubbleClassName}
        style={customBubbleStyles}
      >
        <div
          ref={contentRef}
          className={!isExpanded ? 'max-h-[300px] overflow-hidden' : ''}
        >
          <MessageParser
            contentBlocks={contentBlocks}
            messageId={messageId}
            attachments={files}
            isUserMessage={true}
            onCodeBlockClick={onCodeBlockClick}
            fontSize={chatFontSize}
            isLoadingOrStreaming={fullMessageObject?.isOptimistic || false}
            hasAudio={hasAudio}
          />
        </div>

        {isOverflowing && (
          <button
            onClick={handleExpandToggle}
            className={`flex items-center gap-1 mt-2 transition-colors duration-150 ${isWorkspaceAgentMessage ? 'text-on-surface/75 hover:text-on-surface' : 'text-user-bubble-text/75 hover:text-user-bubble-text'}`}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
          </button>
        )}

        <div className="flex items-center justify-between mt-2 text-xs min-h-[20px]"> 
          <div className="flex items-center flex-nowrap gap-2 whitespace-nowrap"> 
            <span className="text-on-surface-dim opacity-60 whitespace-nowrap shrink-0">
              {formattedTime}
            </span>
            <div
              className={`transition-opacity duration-200 ${showActions ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
              <ActionButtons
                message={fullMessageObject} 
                messageId={messageId}
                showActions={showActions}
                isUser={true}
                actions={actions} 
              />
              {fullMessageObject?.isUser && !isWorkspaceAgentMessage && (
                <div ref={colorPickerRef} className="relative z-20 ml-2">
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="p-1 rounded-md hover:bg-white/10 text-on-surface-dim hover:text-primary transition-colors"
                    title="Change bubble color"
                  >
                    <Palette size={14} />
                  </button>
                  {showColorPicker && (
                    <div className="absolute top-0 right-0 mt-6 p-2 bg-surface rounded-md shadow-lg border border-border-color z-30">
                      <input
                        type="color"
                        value={currentBubbleColor}
                        onChange={handleColorChange}
                        className="w-16 h-8 border-none p-0 cursor-pointer"
                        title="Pick your bubble color"
                      />
                      <button
                        onClick={() => {
                          setCurrentBubbleColor('#3B82F6');
                          authManager.updateSettings({ zPreferences: { customUserBubbleColor: null } });
                          const root = document.documentElement;
                          root.style.removeProperty('--user-bubble');
                          root.style.removeProperty('--user-bubble-text');
                          root.style.removeProperty('--user-bubble-border');
                          setShowColorPicker(false);
                        }}
                        className="ml-2 text-xs text-on-surface-dim hover:text-primary transition-colors"
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

UserBubble.displayName = 'UserBubble';

export default React.memo(UserBubble);